import * as React from 'react';
import { AuthStatus } from 'constants/auth';
import { useAuthModals } from 'hooks/useAuthModals';
import { useViewer } from 'hooks/useViewer';

/**
 * Gates an action behind auth. Anonymous viewers get the login modal (they can
 * toggle to signup), and the action they originally tried is replayed once they
 * authenticate — so the intent survives the detour.
 *
 * Render `authModals` somewhere in the consuming screen.
 *
 *   const { requireAuth, authModals } = useRequireAuth();
 *   <Button onClick={() => requireAuth(() => router.push(SOME_PAGE))} />
 *   {authModals}
 */
export const useRequireAuth = () => {
  const viewer = useViewer();
  const {
    ModalLogin,
    ModalSignup,
    openLoginModal,
    closeLoginModal,
    closeSignupModal,
    isLoginOpen,
    isSignupOpen,
  } = useAuthModals();

  // A ref, not state: the pending action is only ever read inside a callback, so
  // storing it must not trigger a re-render (and avoids the setState(() => fn) trap).
  const pendingActionRef = React.useRef<(() => void) | null>(null);

  const requireAuth = (action: () => void) => {
    // Only Anonymous gates. While the viewer is still Loading we let the action
    // through rather than flash a login modal at someone who is already signed in.
    if (viewer.status === AuthStatus.Anonymous) {
      pendingActionRef.current = action;
      openLoginModal();
      return;
    }

    action();
  };

  const handleAuthComplete = () => {
    // `shouldSkipReload` skips the reload that normally dismissed these, so close
    // them ourselves before running the pending action — otherwise the login modal
    // lingers under whatever the action opens (and the modal lib can't have two open).
    closeLoginModal();
    closeSignupModal();
    const pendingAction = pendingActionRef.current;
    pendingActionRef.current = null;
    pendingAction?.();
  };

  const authModals = (
    <>
      {/*
        `shouldSkipReload` is required: without it the auth forms call
        `router.reload()` on success, which would discard the pending action.
      */}
      <ModalLogin isShowSignupLink shouldSkipReload handleAuthComplete={handleAuthComplete} />
      <ModalSignup isShowLoginLink shouldSkipReload handleAuthComplete={handleAuthComplete} />
    </>
  );

  // Consumers can hide their own open modal while the auth modal is up, so the
  // modal lib never has two open at once (login on top of a detail modal).
  return { requireAuth, authModals, isAuthModalOpen: isLoginOpen || isSignupOpen };
};
